import { useEffect, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent } from "react"
import { useNavigate } from "react-router-dom"
import QRCode from "qrcode"
import { partnersApi } from "../api/endpoints"
import { ApiError } from "../api/client"
import { useTelegram } from "../hooks/useTelegram"
import { useI18n } from "../hooks/useI18n"
import type { MessageKey } from "../i18n"
import type { PartnerProfile } from "../types/api"
import { GarageBackdrop } from "./GarageBackdrop"
import telegramPlane from "../assets/figma-partner/telegram-plane.svg"
import badgePercent from "../assets/figma-partner/badge-percent.svg"
import "./PartnerPage.figma.css"
import { useGarageDisplayVehicle } from "../hooks/useGarageDisplayVehicle"
import { encodePromoStartParam, normalizePromoInput, PROMO_MIN_LENGTH } from "../utils/promo"
import { GARAGE_PREVIEW_VEHICLE } from "./GaragePreviewVehicle"

type PartnerTranslator = (key: MessageKey) => string

function partnerMaskStyle(src: string): CSSProperties {
  return { "--bbf-partner-icon-mask": `url("${src}")` } as CSSProperties
}

function partnerQrMaskStyle(src: string): CSSProperties {
  return { "--bbf-partner-qr-mask": `url("${src}")` } as CSSProperties
}

function buildInviteUrl(profile: PartnerProfile) {
  const url = new URL(profile.affiliate_link!)
  url.searchParams.set("promo", profile.promo_code)
  url.searchParams.set("affiliate", profile.affiliate_link!)
  url.searchParams.set("entry", "garage")
  if (!url.searchParams.has("startapp")) url.searchParams.set("startapp", encodePromoStartParam(profile.promo_code))
  return url.toString()
}

function promoFormatError(value: string, t: PartnerTranslator): string {
  const normalized = normalizePromoInput(value)
  if (!normalized) return ""
  if (normalized.length < PROMO_MIN_LENGTH) return t("partner.promoFormatError")
  return ""
}

function affiliateFormatError(value: string, t: PartnerTranslator): string {
  const raw = value.trim()
  if (!raw) return ""
  const withScheme = raw.includes("://") ? raw : `https://${raw.replace(/^\/+/, "")}`
  try {
    const url = new URL(withScheme)
    const host = url.hostname.toLowerCase().replace(/\.$/, "")
    if (!["t.me", "telegram.me"].includes(host)) return t("partner.affiliateHostError")
    const parts = url.pathname.split("/").filter(Boolean)
    if (!parts.length || parts.some((part) => part === "." || part === "..")) return t("partner.affiliatePathError")
    if (!["http:", "https:"].includes(url.protocol)) return t("partner.affiliateFormatError")
    return ""
  } catch {
    return t("partner.affiliateFormatError")
  }
}

function validationFieldForErrorCode(code?: string): "promo" | "affiliate" | "form" {
  if (["invalid_format", "reserved_code", "user_already_has_code", "code_taken", "partner_conflict"].includes(code || "")) return "promo"
  if (["affiliate_link_required", "invalid_affiliate_link", "invalid_affiliate_host"].includes(code || "")) return "affiliate"
  return "form"
}

export function PartnerPage({ embedded = false, onClose }: { embedded?: boolean; onClose?: () => void } = {}) {
  const navigate = useNavigate()
  const close = () => { if (onClose) onClose(); else navigate(-1) }
  const { tg, hapticNotification, showConfirm, showAlert } = useTelegram()
  const { locale, t } = useI18n()
  const runtimeError = (error: any, fallback: MessageKey) => locale === "ru" && error?.message ? error.message : t(fallback)
  const [profile, setProfile] = useState<PartnerProfile | null>(null)
  const [promo, setPromo] = useState("")
  const [affiliate, setAffiliate] = useState("")
  const [promoError, setPromoError] = useState("")
  const [affiliateError, setAffiliateError] = useState("")
  const [formError, setFormError] = useState("")
  const [busy, setBusy] = useState(false)
  const [qrVisual, setQrVisual] = useState("")
  const [qrDownload, setQrDownload] = useState("")
  const [validatedPair, setValidatedPair] = useState<{ promo: string; affiliate: string } | null>(null)
  const [keyboardMode, setKeyboardMode] = useState(false)
  const dragStartYRef = useRef<number | null>(null)
  const dragOffsetRef = useRef(0)
  const [dragOffset, setDragOffset] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const validationRevisionRef = useRef(0)
  const isIOS = ["ios", "macos"].includes((tg?.platform || "").toLowerCase())
  const saved = Boolean(profile?.affiliate_link)
  const normalizedPromo = normalizePromoInput(promo)
  const normalizedAffiliate = affiliate.trim()
  const valid = Boolean(
    validatedPair &&
    validatedPair.promo === normalizedPromo &&
    validatedPair.affiliate === normalizedAffiliate &&
    !promoError &&
    !affiliateError &&
    !formError
  )
  const formComplete = normalizedPromo.length >= PROMO_MIN_LENGTH && normalizedAffiliate.length > 0
  const partnerState = saved
    ? "saved"
    : promoError || affiliateError || formError
      ? "invalid"
      : !formComplete
        ? "incomplete"
        : valid
          ? "valid"
          : "incomplete"
  const { vehicle: garageVehicle, isLoading: isGarageVehicleLoading } = useGarageDisplayVehicle(true, !embedded)

  const setSheetDragOffset = (value: number) => {
    const next = Math.max(0, value)
    dragOffsetRef.current = next
    setDragOffset(next)
  }

  const handleGrabberPointerDown = (event: ReactPointerEvent<HTMLElement>) => {
    if (!embedded || (event.pointerType === "mouse" && event.button !== 0)) return
    dragStartYRef.current = event.clientY
    setSheetDragOffset(0)
    setIsDragging(true)
    event.currentTarget.setPointerCapture(event.pointerId)
  }

  const handleGrabberPointerMove = (event: ReactPointerEvent<HTMLElement>) => {
    if (!embedded || dragStartYRef.current === null) return
    event.preventDefault()
    setSheetDragOffset(event.clientY - dragStartYRef.current)
  }

  const finishGrabberDrag = (event: ReactPointerEvent<HTMLElement>, allowClose: boolean) => {
    if (!embedded) return
    const shouldClose = allowClose && dragOffsetRef.current >= 72
    dragStartYRef.current = null
    setSheetDragOffset(0)
    setIsDragging(false)
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
    if (shouldClose) close()
  }

  useEffect(() => { partnersApi.getMe().then((value) => {
    setProfile(value); setPromo(value?.promo_code || ""); setAffiliate(value?.affiliate_link || "")
  }).catch((e) => setFormError(runtimeError(e, "partner.loadError"))) }, [locale])

  useEffect(() => {
    if (saved) return

    const revision = ++validationRevisionRef.current
    const promoSnapshot = normalizedPromo
    const affiliateSnapshot = normalizedAffiliate
    const nextPromoError = promoFormatError(promoSnapshot, t)
    const nextAffiliateError = affiliateFormatError(affiliateSnapshot, t)

    setValidatedPair(null)
    setPromoError(nextPromoError)
    setAffiliateError(nextAffiliateError)
    setFormError("")

    if (!promoSnapshot || !affiliateSnapshot || nextPromoError || nextAffiliateError) return

    void partnersApi.validate(promoSnapshot, affiliateSnapshot)
      .then(() => {
        if (revision !== validationRevisionRef.current) return
        setValidatedPair({ promo: promoSnapshot, affiliate: affiliateSnapshot })
      })
      .catch((e: any) => {
        if (revision !== validationRevisionRef.current) return
        const message = runtimeError(e, "partner.checkInputError")
        const field = e instanceof ApiError ? validationFieldForErrorCode(e.errorCode) : "form"
        if (field === "promo") setPromoError(message)
        else if (field === "affiliate") setAffiliateError(message)
        else setFormError(message)
      })
  }, [normalizedPromo, normalizedAffiliate, saved, locale])

  useEffect(() => {
    if (!saved || !profile) {
      setQrVisual("")
      setQrDownload("")
      return
    }
    let cancelled = false
    setQrVisual("")
    setQrDownload("")
    const inviteUrl = buildInviteUrl(profile)
    Promise.all([
      QRCode.toDataURL(inviteUrl, {
        width: 220,
        margin: 1,
        errorCorrectionLevel: "H",
        color: { dark: "#000000ff", light: "#00000000" },
      }),
      QRCode.toDataURL(inviteUrl, { width: 220, margin: 1, errorCorrectionLevel: "H" }),
    ])
      .then(([visual, downloadable]) => {
        if (cancelled) return
        setQrVisual(visual)
        setQrDownload(downloadable)
      })
      .catch(() => {
        if (cancelled) return
        setQrVisual("")
        setQrDownload("")
      })
    return () => { cancelled = true }
  }, [saved, profile])

  const save = async () => {
    const promoSnapshot = normalizePromoInput(promo)
    const affiliateSnapshot = affiliate.trim()
    if (
      !validatedPair ||
      validatedPair.promo !== promoSnapshot ||
      validatedPair.affiliate !== affiliateSnapshot ||
      busy
    ) return

    setBusy(true); setFormError("")
    try {
      const next = profile
        ? await partnersApi.rebind(affiliateSnapshot)
        : await partnersApi.createPromoCode(promoSnapshot, affiliateSnapshot)
      setProfile(next)
      setPromo(next.promo_code)
      setAffiliate(next.affiliate_link || "")
      setValidatedPair(null)
      hapticNotification("success")
    } catch (e: any) {
      const message = runtimeError(e, "partner.checkInputError")
      const field = e instanceof ApiError ? validationFieldForErrorCode(e.errorCode) : "form"
      if (field === "promo") setPromoError(message)
      else if (field === "affiliate") setAffiliateError(message)
      else setFormError(message)
      hapticNotification("error")
    }
    finally { setBusy(false) }
  }
  const reset = async () => {
    if (!profile || !await showConfirm(t("partner.resetConfirm"))) return
    validationRevisionRef.current += 1
    setValidatedPair(null)
    setPromoError("")
    setAffiliateError("")
    setFormError("")
    setProfile(await partnersApi.unbind()); setAffiliate(""); setQrVisual(""); setQrDownload("")
  }
  const download = () => {
    if (!qrDownload || !profile) return
    const link = document.createElement("a"); link.href = qrDownload; link.download = `beepapp-${profile.promo_code}.png`; link.click()
  }

  useEffect(() => {
    const syncKeyboardMode = (event: Event) => {
      setKeyboardMode((event as CustomEvent<boolean>).detail === true)
    }

    setKeyboardMode(document.documentElement.dataset.bbGarageKeyboard === "true")
    window.addEventListener("bb:garage-keyboard", syncKeyboardMode as EventListener)
    return () => window.removeEventListener("bb:garage-keyboard", syncKeyboardMode as EventListener)
  }, [])

  if (!embedded && isGarageVehicleLoading && !garageVehicle) return <div className="bbf-garage-loading" aria-label={t("partner.loadingAria")} />
  const displayVehicle = garageVehicle ?? GARAGE_PREVIEW_VEHICLE

  const sheet = (
    <section
      className={`partner-sheet state-${partnerState} ${keyboardMode ? "keyboard-mode" : ""}${embedded && isDragging ? " is-dragging" : ""}`}
      style={embedded ? ({ "--bbf-partner-drag-y": `${dragOffset}px` } as CSSProperties) : undefined}
      onClick={(event) => event.stopPropagation()}
    >
      <div className="partner-sheet-scroll">
      <i
        className="partner-grabber"
        onPointerDown={embedded ? handleGrabberPointerDown : undefined}
        onPointerMove={embedded ? handleGrabberPointerMove : undefined}
        onPointerUp={embedded ? (event) => finishGrabberDrag(event, true) : undefined}
        onPointerCancel={embedded ? (event) => finishGrabberDrag(event, false) : undefined}
      /><h1>{t("partner.title")}</h1>
      <div className="partner-offer"><span className="partner-offer-icon" style={partnerMaskStyle(badgePercent)} aria-hidden="true"/><p>{t("partner.offerLine1")}<br/>{t("partner.offerLine2")}<br/>{t("partner.offerLine3")}</p></div>
      {!saved && <button className="partner-connect" onClick={() => showAlert(t("partner.connectUnavailable"))}><span className="partner-connect-icon" style={partnerMaskStyle(telegramPlane)} aria-hidden="true"/><span className="partner-connect-label">{t("partner.connectTelegram")}</span></button>}
      {saved && profile ? <>
        <div className="partner-qr-card">
          <strong>BeepApp</strong>
          <div className="partner-qr-slot">{qrVisual && <span className="partner-qr-render" style={partnerQrMaskStyle(qrVisual)} role="img" aria-label={t("partner.qrAria")}/>}</div>
          <span>{profile.promo_code}</span>
        </div>
        <button className="partner-back" onClick={() => close()}>{t("common.back")}</button><button className="partner-save" onClick={download}>{t("partner.download")}</button>
      </> : <>
        <h2 className="partner-promo-heading">{t("partner.promoHeading")}</h2><p className="partner-promo-helper">{t("partner.promoHelperLine1")}<br/>{t("partner.promoHelperLine2")}</p>
        <input className={`partner-promo-input${promoError ? " is-invalid" : ""}`} aria-label={t("partner.promoAria")} aria-invalid={Boolean(promoError)} value={promo} disabled={Boolean(profile)} placeholder="PROMOCODE" onChange={(e) => { validationRevisionRef.current += 1; setPromo(normalizePromoInput(e.target.value)); setValidatedPair(null); setPromoError(""); setFormError("") }}/>
        <h2 className="partner-affiliate-heading">{t("partner.affiliateHeading")}</h2><p className="partner-affiliate-helper">{t("partner.affiliateHelper")}</p>
        <input className={`partner-affiliate-input${affiliateError ? " is-invalid" : ""}`} aria-label={t("partner.affiliateAria")} aria-invalid={Boolean(affiliateError)} value={affiliate} placeholder="https://t.me/..." onChange={(e) => { validationRevisionRef.current += 1; setAffiliate(e.target.value); setAffiliateError(""); setValidatedPair(null); setFormError("") }}/>
        {(promoError || affiliateError || formError) && <p className="partner-error-a11y" role="alert">{promoError || affiliateError || formError}</p>}<small className="partner-qr-note">{t("partner.qrNoteLine1")}<br/>{t("partner.qrNoteLine2")}</small>
        <button className="partner-back" onClick={() => close()}>{t("common.back")}</button><button className="partner-save" disabled={!valid || busy} onClick={save}>{t("partner.save")}</button>
      </>}
      </div>
    </section>
  )

  if (embedded) {
    return (
      <div className={`partner-final partner-embedded ${isIOS ? "platform-ios" : "platform-android"}`}>
        <div className="partner-embedded-overlay" onClick={close} aria-hidden="true" />
        {sheet}
      </div>
    )
  }

  return (
    <main className={`partner-final ${isIOS ? "platform-ios" : "platform-android"}`} onClick={(event) => {
      const target = event.target
      if (target instanceof Element && target.closest(".partner-sheet, button, input, a")) return
      close()
    }}>
      <GarageBackdrop
        vehicle={displayVehicle}
        isIOS={isIOS}
        active={Boolean(garageVehicle?.garage_is_active && garageVehicle?.is_owner_viewing)}
        onBack={close}
        headerRightActionLabel={saved ? t("partner.reset") : undefined}
        onHeaderRightAction={saved ? () => void reset() : undefined}
      />
      {sheet}
    </main>
  )
}